import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  CurrencyGbp, 
  CaretDown, 
  CaretUp,
  CheckCircle,
  Truck,
  Wrench,
  RoadHorizon,
  Factory,
  Users,
  GraduationCap,
  Heart,
  Bicycle,
  FirstAid,
  Car,
  Recycle,
  Broom,
  Star
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Seo } from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  'Operations': Truck,
  'Workshop': Wrench,
  'Highways': RoadHorizon,
  'Waste Transfer': Factory,
  'Management': Users,
  'Training': GraduationCap,
  'Recycling': Recycle,
  'Cleansing': Broom,
};

const benefits = [
  { icon: Heart, title: 'Company Pension', description: '3-5% employer contribution' },
  { icon: FirstAid, title: 'Free Life Policy', description: 'Company-paid life assurance' },
  { icon: Bicycle, title: 'Cycle to Work', description: 'Tax-free bike scheme' },
  { icon: GraduationCap, title: 'Career Development', description: 'Formal qualifications & vocational certificates' },
  { icon: Truck, title: 'Driver Training', description: 'In-house Driver Training Academy' },
  { icon: Car, title: 'Free Parking', description: 'On-site parking available' },
  { icon: Users, title: 'Employee Assistance', description: '24/7 support for you and your household' },
  { icon: CheckCircle, title: 'Gym Discount', description: 'Access to local gym facilities' },
];

const JobCard = ({ job, autoExpand = false, cardRef = null }) => {
  const [isExpanded, setIsExpanded] = useState(autoExpand);
  const [showApply, setShowApply] = useState(false);
  const [appForm, setAppForm] = useState({ first_name: '', last_name: '', email: '', phone: '', cover_letter: '', cv_base64: null, cv_filename: null });
  const [submitting, setSubmitting] = useState(false);
  const IconComponent = iconMap[job.department] || Briefcase;

  const handleCvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAppForm(prev => ({ ...prev, cv_base64: reader.result.split(',')[1], cv_filename: file.name }));
    reader.readAsDataURL(file);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!appForm.first_name || !appForm.last_name || !appForm.email || !appForm.cover_letter) { toast.error('Please fill in all required fields'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/apply`, { job_id: job.id, ...appForm });
      toast.success('Application submitted successfully!');
      setShowApply(false);
      setAppForm({ first_name: '', last_name: '', email: '', phone: '', cover_letter: '', cv_base64: null, cv_filename: null });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit application'); }
    finally { setSubmitting(false); }
  };

  return (
    <>
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`bg-white rounded-2xl border ${job.featured ? 'border-lime-300 ring-1 ring-lime-200' : 'border-neutral-200'} ${autoExpand ? 'ring-2 ring-lime-400 shadow-lg' : ''} overflow-hidden hover:shadow-lg transition-shadow duration-300 scroll-mt-32`}
      data-testid={`job-card-${job.id}`}
      id={`job-${job.slug || job.id}`}
    >
      {job.featured && (
        <div className="bg-lime-600 text-white px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5" data-testid={`job-featured-banner-${job.id}`}>
          <Star size={12} weight="fill" />
          Featured Opportunity
        </div>
      )}
      <div className="p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-lime-50">
              <IconComponent size={28} weight="duotone" className="text-lime-600" />
            </div>
            <div>
              <h3 className="font-outfit font-semibold text-xl text-neutral-900 mb-1">
                {job.title}
              </h3>
              <p className="text-sm text-neutral-500">Ref: {job.ref}</p>
            </div>
          </div>
          <Button onClick={() => setShowApply(true)} className="bg-lime-600 hover:bg-lime-700 text-white shrink-0" data-testid={`apply-btn-${job.id}`}>
            Apply Now
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="flex items-center gap-2 text-neutral-600 text-sm">
            <CurrencyGbp size={18} className="text-lime-600" />
            <span>{job.salary}</span>
          </div>
          <div className="flex items-center gap-2 text-neutral-600 text-sm">
            <MapPin size={18} className="text-lime-600" />
            <span>{job.location}</span>
          </div>
          <div className="flex items-center gap-2 text-neutral-600 text-sm">
            <Clock size={18} className="text-lime-600" />
            <span>{job.hours}</span>
          </div>
          <div className="flex items-center gap-2 text-neutral-600 text-sm">
            <Briefcase size={18} className="text-lime-600" />
            <span>{job.contract}</span>
          </div>
        </div>

        <p className="text-neutral-600 mb-4">{job.description}</p>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-lime-600 font-medium hover:text-lime-700 transition-colors"
          data-testid={`job-expand-${job.id}`}
        >
          {isExpanded ? 'Show less' : 'View full details'}
          {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
        </button>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6 pt-6 border-t border-neutral-100"
          >
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-neutral-900 mb-3">Key Responsibilities</h4>
                <ul className="space-y-2">
                  {(job.responsibilities || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-neutral-600 text-sm">
                      <CheckCircle size={18} weight="fill" className="text-lime-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-neutral-900 mb-3">Requirements</h4>
                <ul className="space-y-2">
                  {(job.requirements || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-neutral-600 text-sm">
                      <CheckCircle size={18} weight="fill" className="text-lime-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 p-4 bg-neutral-50 rounded-xl flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-neutral-600">
                <strong>Closing Date:</strong> {new Date(job.closing_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const url = `${window.location.origin}/careers/${job.slug || job.id}`;
                    navigator.clipboard?.writeText(url).then(
                      () => toast.success('Link copied'),
                      () => toast.error('Could not copy link'),
                    );
                  }}
                  className="text-xs text-neutral-600 hover:text-lime-700 underline underline-offset-2"
                  data-testid={`copy-link-${job.id}`}
                >
                  Copy share link
                </button>
                <Button onClick={() => setShowApply(true)} className="bg-lime-600 hover:bg-lime-700 text-white" data-testid={`apply-btn-expanded-${job.id}`}>
                  Apply Now
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>

    {/* Apply Now Modal */}
    {showApply && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm" data-testid={`apply-modal-${job.id}`}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 lg:p-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="p-2.5 rounded-xl bg-lime-50"><Briefcase size={24} className="text-lime-600" /></div>
            <div><h2 className="font-outfit font-bold text-xl text-neutral-900">Apply for: {job.title}</h2><p className="text-sm text-neutral-500 mt-0.5">Ref: {job.ref} | {job.location}</p></div>
          </div>
          <form onSubmit={handleApply} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-sm text-neutral-700">First Name *</Label><Input value={appForm.first_name} onChange={e => setAppForm({...appForm, first_name: e.target.value})} required className="mt-1 border-neutral-200" data-testid="apply-firstname" /></div>
              <div><Label className="text-sm text-neutral-700">Last Name *</Label><Input value={appForm.last_name} onChange={e => setAppForm({...appForm, last_name: e.target.value})} required className="mt-1 border-neutral-200" data-testid="apply-lastname" /></div>
            </div>
            <div><Label className="text-sm text-neutral-700">Email Address *</Label><Input type="email" value={appForm.email} onChange={e => setAppForm({...appForm, email: e.target.value})} required className="mt-1 border-neutral-200" data-testid="apply-email" /></div>
            <div><Label className="text-sm text-neutral-700">Phone Number</Label><Input value={appForm.phone} onChange={e => setAppForm({...appForm, phone: e.target.value})} className="mt-1 border-neutral-200" data-testid="apply-phone" /></div>
            <div><Label className="text-sm text-neutral-700">Cover Letter / Supporting Statement *</Label><Textarea value={appForm.cover_letter} onChange={e => setAppForm({...appForm, cover_letter: e.target.value})} required rows={4} className="mt-1 border-neutral-200" placeholder="Tell us why you're interested in this role and what makes you a great fit..." data-testid="apply-coverletter" /></div>
            <div>
              <Label className="text-sm text-neutral-700">Upload CV</Label>
              <label className="mt-1 flex items-center justify-center w-full h-20 border-2 border-dashed border-neutral-300 rounded-xl cursor-pointer hover:border-lime-400 bg-neutral-50 transition-colors">
                {appForm.cv_filename ? (
                  <div className="text-center"><CheckCircle size={20} weight="fill" className="mx-auto text-lime-500 mb-1" /><p className="text-sm text-neutral-700">{appForm.cv_filename}</p></div>
                ) : (
                  <div className="text-center"><Briefcase size={20} className="mx-auto text-neutral-400 mb-1" /><p className="text-sm text-neutral-500">Click to upload CV (PDF, DOC, DOCX)</p></div>
                )}
                <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={handleCvFile} data-testid="apply-cv-input" />
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowApply(false)} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-lime-600 hover:bg-lime-700 text-white" data-testid="submit-application">{submitting ? 'Submitting...' : 'Submit Application'}</Button>
            </div>
          </form>
        </motion.div>
      </div>
    )}
    </>
  );
};

export default function CareersPage() {
  const { jobSlug } = useParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apprenticeships, setApprenticeships] = useState(null);
  const targetJobRef = useRef(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job: '',
    message: '',
  });

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${API}/jobs`);
        setJobs(response.data);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    const fetchApprenticeships = async () => {
      try {
        const res = await axios.get(`${API}/page-content/careers_apprenticeships`);
        setApprenticeships(res.data || {});
      } catch (error) {
        console.error('Error fetching apprenticeships content:', error);
        setApprenticeships({});
      }
    };
    fetchJobs();
    fetchApprenticeships();
  }, []);

  // When the URL points at a specific job (e.g. /careers/hgv-driver-bristol),
  // scroll to that card once the job list has loaded. The card itself
  // auto-expands via the `autoExpand` prop below.
  useEffect(() => {
    if (!jobSlug || loading || !jobs.length) return;
    const target = jobs.find(j => (j.slug || j.id) === jobSlug);
    if (!target) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`job-${target.slug || target.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, [jobSlug, loading, jobs]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleJobChange = (value) => {
    setFormData((prev) => ({ ...prev, job: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Thank you for your application! We will be in touch soon.');
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      job: '',
      message: '',
    });
  };

  return (
    <main className="pt-24 bg-white" data-testid="careers-page">
      <Seo title="Careers" description="Join NSEC — local careers in environmental services, driver training, operations and more. Real progression and apprenticeships across the South West." path="/careers" />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=2000)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 via-neutral-900/80 to-neutral-900/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="accent-font text-lime-400 mb-4">Careers at NSEC</p>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-6">
              Start Your Journey <span className="text-lime-400">With Us</span> Today
            </h1>
            <p className="text-neutral-300 text-lg">
              Join our team and be part of a community dedicated to making a positive impact 
              in North Somerset. We offer rewarding careers with excellent benefits and 
              opportunities for growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-20 lg:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="accent-font text-lime-600 mb-4">Why Join NSEC?</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-6">
              Empowering North Somerset <span className="text-lime-600">Talent</span>
            </h2>
            <p className="text-neutral-600 text-lg max-w-3xl mx-auto">
              At NSEC, we&apos;re more than just a company – we&apos;re a community dedicated to making 
              a positive impact. Our commitment extends beyond our services; it&apos;s at the heart 
              of everything we do, including our approach to employment.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white rounded-2xl border border-neutral-200 hover:shadow-lg transition-shadow duration-300"
                data-testid={`benefit-card-${index}`}
              >
                <div className="p-3 rounded-xl bg-lime-50 w-fit mb-4">
                  <benefit.icon size={24} weight="duotone" className="text-lime-600" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">{benefit.title}</h3>
                <p className="text-neutral-600 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Vacancies */}
      <section id="vacancies" className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="accent-font text-lime-600 mb-4">Current Vacancies</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-neutral-900 tracking-tight">
              Open <span className="text-lime-600">Positions</span>
            </h2>
          </motion.div>

          {loading ? (
            <div className="text-center py-12 text-neutral-500">Loading jobs...</div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Briefcase size={48} className="mx-auto mb-4 text-neutral-300" />
              <p>No current vacancies. Please check back later.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {jobs.map((job) => (
                <JobCard key={job.id} job={job} autoExpand={jobSlug && (job.slug || job.id) === jobSlug} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Career Development */}
      <section className="py-20 lg:py-24 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="accent-font text-lime-400 mb-4">{apprenticeships?.overline || 'Progression'}</p>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white tracking-tight mb-6">
                {apprenticeships?.heading || 'Apprenticeships &'} <span className="text-lime-400">{apprenticeships?.heading_highlight || 'Development'}</span>
              </h2>
              {(apprenticeships?.paragraphs && apprenticeships.paragraphs.length > 0 ? apprenticeships.paragraphs : [
                'North Somerset Environment Company is committed to training and development across all areas of our business. We currently have multiple Apprentices learning their trade whilst gaining vital on-the-job experience.',
                'We support our workforce in their career aspirations, with several of our Supervisors and Managers having worked through our ranks from our collections workforce.',
              ]).map((para, idx) => (
                <p key={idx} className="text-neutral-300 leading-relaxed mb-6" data-testid={`apprenticeships-para-${idx}`}>
                  {para}
                </p>
              ))}
              {apprenticeships?.bullets && apprenticeships.bullets.length > 0 && (
                <ul className="space-y-2 mt-6" data-testid="apprenticeships-bullets">
                  {apprenticeships.bullets.map((b, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-neutral-300">
                      <CheckCircle size={20} weight="fill" className="text-lime-400 flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src={apprenticeships?.image_1 || 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?q=80&w=500'}
                alt="Apprenticeships at NSEC"
                className="rounded-2xl object-cover h-48 w-full"
                data-testid="apprenticeships-image-1"
              />
              <img
                src={apprenticeships?.image_2 || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?q=80&w=500'}
                alt="Career development at NSEC"
                className="rounded-2xl object-cover h-48 w-full mt-8"
                data-testid="apprenticeships-image-2"
              />
              <img
                src={apprenticeships?.image_3 || 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=500'}
                alt="Team collaboration"
                className="rounded-2xl object-cover h-48 w-full"
                data-testid="apprenticeships-image-3"
              />
              <img
                src={apprenticeships?.image_4 || 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=500'}
                alt="On-the-job training"
                className="rounded-2xl object-cover h-48 w-full mt-8"
                data-testid="apprenticeships-image-4"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 lg:py-24 bg-neutral-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="accent-font text-lime-600 mb-4">Apply Now</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-4">
              Join Our <span className="text-lime-600">Team</span>
            </h2>
            <p className="text-neutral-600">
              We look forward to hearing from you. Fill out the form below or email your CV to{' '}
              <a href="mailto:jobs@nsecltd.co.uk" className="text-lime-600 hover:underline">
                jobs@nsecltd.co.uk
              </a>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-2xl border border-neutral-200 shadow-lg"
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="careers-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-neutral-700">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="bg-neutral-50 border-neutral-200"
                    placeholder="John"
                    data-testid="careers-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-neutral-700">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="bg-neutral-50 border-neutral-200"
                    placeholder="Smith"
                    data-testid="careers-last-name"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-neutral-700">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-neutral-50 border-neutral-200"
                    placeholder="john@example.com"
                    data-testid="careers-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-neutral-700">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-neutral-50 border-neutral-200"
                    placeholder="07917 092 785"
                    data-testid="careers-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-700">Position Applying For *</Label>
                <Select value={formData.job} onValueChange={handleJobChange} required>
                  <SelectTrigger className="bg-neutral-50 border-neutral-200" data-testid="careers-job-select">
                    <SelectValue placeholder="Select a position" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-neutral-200">
                    {jobs.map((job) => (
                      <SelectItem key={job.id} value={job.title} className="cursor-pointer">
                        {job.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="general" className="cursor-pointer">General Application</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-neutral-700">Cover Letter / Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-neutral-50 border-neutral-200 resize-none"
                  placeholder="Tell us about yourself and why you'd be a great fit..."
                  data-testid="careers-message"
                />
              </div>

              <p className="text-sm text-neutral-500">
                If you have a CV, please email it to{' '}
                <a href="mailto:jobs@nsecltd.co.uk" className="text-lime-600 hover:underline">
                  jobs@nsecltd.co.uk
                </a>{' '}
                or call <a href="tel:07917092785" className="text-lime-600 hover:underline">07917 092 785</a>
              </p>

              <Button
                type="submit"
                className="w-full bg-lime-600 hover:bg-lime-700 text-white font-semibold py-6 rounded-full"
                data-testid="careers-submit-button"
              >
                Submit Application
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
